import React, { useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Typography, FormControl } from "@mui/material";
import { useIntl } from "react-intl";
import { styles, useStyles } from "./SmartSort.styles";
import DropdownMenu from "./common/DropdownMenu";
import {
  selectProductListsFetched,
  selectCurrentPage,
  selectContentSlots,
  selectProductListSequenceMode,
  selectProductListEditSequence,
} from "store/product-list/ProductListSelectors";
import ButtonComponent from "./common/ButtonComponent";
import {
  selectIsFetchedBySearch,
  selectProductListBySearch,
  selectProductColors,
  selectSelectedColorOption,
} from "store/product/ProductSelectors";
import {
  getCurrentPageProductIds,
  getCurrentPageProductsHavingLeadingColor,
} from "utils/ProductUtil";
import { getColorOptions } from "utils/ProductUtil";
import { setSelectedColorOption } from "store/product/ProductActions";
import { selectCurrentCategory } from "store/category/CategorySelectors";
import { ConfirmationBulkLeadingColorActionModalId } from "./ConfirmationBulkLeadingColorAction";
import { setModalState } from "store/modal/ModalActions";

const BulkLeadingColorMgmt = () => {
  const classes = useStyles();
  const intl = useIntl();
  const { bodyWrap } = styles();
  const dispatch = useDispatch();
  const currentPage = useSelector(selectCurrentPage);
  const isFetchedBySearch = useSelector(selectIsFetchedBySearch);
  const productsBySearch = useSelector(selectProductListBySearch);
  const sequenceMode = useSelector(selectProductListSequenceMode);
  const products = useSelector(selectProductListsFetched);
  const editProducts = useSelector(selectProductListEditSequence);
  const contentSlots = useSelector(selectContentSlots);
  const productColorsData = useSelector(selectProductColors);
  const selectedColorOption = useSelector(selectSelectedColorOption);
  const currentCategoryId = useSelector(selectCurrentCategory);

  const currentPageProductIds = useMemo(
    () =>
      getCurrentPageProductIds(
        contentSlots,
        currentPage,
        isFetchedBySearch,
        productsBySearch,
        sequenceMode,
        products,
        editProducts,
      ),
    [
      contentSlots,
      currentPage,
      isFetchedBySearch,
      productsBySearch,
      sequenceMode,
      products,
      editProducts,
    ],
  );

  const currentPageProductsHavingLeadingColor = useMemo(
    () =>
      getCurrentPageProductsHavingLeadingColor(
        productColorsData,
        currentPageProductIds,
        currentCategoryId,
      ),
    [productColorsData, currentPageProductIds, currentCategoryId],
  );

  const colorOptions = useMemo(
    () => [
      {
        label: "None",
        value: "None",
        disabled: currentPageProductsHavingLeadingColor.length === 0,
      },
      ...getColorOptions(productColorsData, currentPageProductIds),
    ],
    [
      productColorsData,
      currentPageProductIds,
      currentPageProductsHavingLeadingColor.length,
    ],
  );

  const handleColorChange = useCallback(
    (event) => {
      dispatch(setSelectedColorOption({ colorId: event.target.value }));
    },
    [dispatch],
  );

  const applyColorMgmt = useCallback(() => {
    dispatch(
      setModalState(ConfirmationBulkLeadingColorActionModalId, true, {
        currentPageProductIds,
        currentPageProductsHavingLeadingColor,
      }),
    );
  }, [dispatch, currentPageProductIds, currentPageProductsHavingLeadingColor]);

  const isDisabled = useMemo(() => {
    return (
      !selectedColorOption ||
      (currentPageProductsHavingLeadingColor.length === 0 &&
        selectedColorOption === "None")
    );
  }, [selectedColorOption, currentPageProductsHavingLeadingColor]);

  return (
    <div className={classes.root}>
      <div>
        <div className={classes.sortClass}>
          <FormControl sx={bodyWrap}>
            <label>
              <Typography variant="subHeader">
                {intl.formatMessage({
                  id: "colorManagementModal.leadingColor",
                  defaultMessage: "Leading Color",
                })}
              </Typography>
            </label>
            <DropdownMenu
              menuId="ColorManagementTools"
              value={selectedColorOption}
              changeHandler={handleColorChange}
              colorOptions={colorOptions}
            />
          </FormControl>
          <ButtonComponent
            color="primary"
            variant="contained"
            disabled={isDisabled}
            onClick={applyColorMgmt}
          >
            {intl.formatMessage({
              id: "colorManagementButton.label",
              defaultMessage: "Apply",
            })}
          </ButtonComponent>
        </div>
      </div>
    </div>
  );
};

export default React.memo(BulkLeadingColorMgmt);
